import { StripeError } from '@stripe/stripe-js';
import { AxiosError } from 'axios';
import { mapTimezoneToAPIName } from 'domains/calendars/dataNormalization';
import { ContactRelationship, InsuranceSubscriberRelationship } from 'domains/client';
import { scheduleConsultationFromSignupV2 } from 'domains/consultations/freeConsultationSchedule';
import { queryClient } from 'domains/data';
import {
  scheduleEvaluationFromSignupV3,
  ScheduleSignupEvaluationResponse,
  SignupSchedule,
} from 'domains/evaluations/evaluationSchedule';
import {
  EnterInsuranceFormV4,
  MEDICAID_RELATIONSHIP_TO_INSURED_NUMBER,
  PayerTypes,
  InsuranceType,
  SalesforcePayerTypes,
  InsuranceStatus,
  VerificationTypes,
} from 'domains/signup/data/types';
import { mergeCommunicationConcerns } from 'domains/signupv4/Mini-Screener/data/areaDetection';
import { checkEligibility, mapSignupV4ToMedicaidInsuranceForm } from 'domains/signupv4/Insurance/queries';
import {
  getSalesforceLead,
  updateSalesforceLead,
  UpsertInsuranceLeadResponseV4,
  upsertSalesforceInsuranceLead,
} from 'domains/signupv4/Leads/queries';
import { SignupLeadProgressV4, SignupLeadV4 } from 'domains/signupv4/Leads/types';
import { CompletedSignupV4Data, IncompleteSignupV4Data } from 'domains/signupv4/Parent/signupMachineV4';
import { CompletedScheduleMachineContext, ScheduleMachineInput } from 'domains/signupv4/schedule/scheduleMachineV4';
import { debounce, isUndefined } from 'lodash';
import { extractDate } from 'utils/date';
import { removeWhitespaces } from 'utils/phoneNumber';
import { StateAbbrev } from 'utils/states';
import { fromPromise } from 'xstate';
import { EligibilityCheckResult, EligibilityCheckResultWithPCP } from '../Insurance/types';
import { TypeInsuranceFields } from 'types/contentful';
import { EvaluationDuration } from 'domains/calendars/types';
import { SUBSCRIBER_RELATIONSHIP_CODE_MAP } from 'domains/signupDirect/businessLogic/relationships';

export const loadLeadDataLogic = fromPromise<SignupLeadProgressV4 | null, { leadID: string }>(({ input }) => {
  return getSalesforceLead(input.leadID);
});

function getInsuranceType(_insuranceType: InsuranceType, useInsurance: boolean) {
  if (!useInsurance) return SalesforcePayerTypes.SELF_PAY;

  switch (_insuranceType) {
    case 'medicaid':
      return SalesforcePayerTypes.MEDICAID;
    case 'medicare':
      return SalesforcePayerTypes.MEDICARE;
    // case 'private':
    //   return SalesforcePayerTypes.COMMERCIAL;
    // case 'work':
    //   return SalesforcePayerTypes.COMMERCIAL;
    case 'other':
      return SalesforcePayerTypes.OTHER;
    default:
      return SalesforcePayerTypes.UNKNOWN;
  }
}

// With contentful types
export function getSFInsuranceType(insuranceType: TypeInsuranceFields['insuranceType'], isPrivatePay: boolean) {
  if (isPrivatePay) return SalesforcePayerTypes.SELF_PAY;

  switch (insuranceType) {
    case 'Medicaid':
      return SalesforcePayerTypes.MEDICAID;
    case 'Medicare':
      return SalesforcePayerTypes.MEDICARE;

    case 'Commercial':
      return SalesforcePayerTypes.COMMERCIAL;

    default:
      return SalesforcePayerTypes.UNKNOWN;
  }
}

export const upsertInsuranceLeadLogic = fromPromise<UpsertInsuranceLeadResponseV4, CompletedSignupV4Data>(
  ({ input }) => {
    const insuranceType = getInsuranceType(input.insuranceInfo.insuranceType, input.insuranceInfo.useInsurance);

    const getStatusAndType = () => {
      if (!input.insuranceInfo?.insuranceForm) {
        return {
          insuranceStatus: InsuranceStatus.NOT_CHECKED,
        };
      }

      switch (input.insuranceInfo.insuranceEligibility) {
        case EligibilityCheckResult.IN_NETWORK:
          return {
            insuranceStatus: InsuranceStatus.ACTIVE,
            verificationType: VerificationTypes.AUTOMATED,
          };
        case EligibilityCheckResult.INACTIVE:
          return {
            insuranceStatus: InsuranceStatus.INACTIVE,
            verificationType: VerificationTypes.AUTOMATED,
          };
        default:
          return {
            insuranceStatus: InsuranceStatus.NOT_CHECKED,
          };
      }
    };

    const { insuranceStatus, verificationType } = getStatusAndType();

    return upsertSalesforceInsuranceLead({
      insuranceLeadID: input.insuranceLeadID ?? null,
      leadID: input.leadID,
      insuranceType,
      payerPlanName: input.insuranceInfo?.insuranceForm?.payerName,
      payerForClaims: input.insuranceInfo?.insuranceForm?.payerId,
      memberID: input.insuranceInfo?.insuranceForm?.memberId,
      subscriber: input.insuranceInfo?.insuranceForm
        ? {
            firstName: input.insuranceInfo.insuranceForm.firstName,
            lastName: input.insuranceInfo.insuranceForm.lastName,
            dateOfBirth: input.insuranceInfo.insuranceForm.dob as Date,
            relationshipToClient: SUBSCRIBER_RELATIONSHIP_CODE_MAP[InsuranceSubscriberRelationship.Self],
          }
        : undefined,
      insuranceStatus,
      verificationType,
    });
  },
);

type ScheduleInput = Required<CompletedScheduleMachineContext & ScheduleMachineInput['machineConfig']>;

export const scheduleConsultationLogic = fromPromise<void, ScheduleInput>(({ input }) => {
  return scheduleConsultationFromSignupV2({
    consultationDate: input.consultationDate,
    consultationTimezone: input.consultationTimezone,
    schedulingId: input.schedulingID,
    appointmentType: 'initial-consult',
    calendarID: input.calendarID,
  });
});

export type ScheduleEvaluationErrorResponse = {
  originalError: AxiosError<{ error?: StripeError; message: string; status: string }>;
};

export const scheduleEvaluationLogic = fromPromise<void | ScheduleSignupEvaluationResponse, ScheduleInput>(
  ({ input }) => {
    const isAdult = input.target === 'adult';
    const clientFirstName =
      !isAdult && input.miniScreenerInfo?.clientFirstName ? input.miniScreenerInfo.clientFirstName : input.firstName;
    const clientLastName =
      !isAdult && input.miniScreenerInfo?.clientLastName ? input.miniScreenerInfo.clientLastName : input.lastName;

    const skipPayment = input.useInsurance === true && input.insuranceEligibility === EligibilityCheckResult.IN_NETWORK;

    const schedules: SignupSchedule[] = [
      {
        duration: EvaluationDuration.Single,
        timeSlot: input.evaluationDate.toISOString(),
        calendarID: (input.calendarID as number).toString(),
      },
    ];

    //@ts-expect-error not providing some new required fields used only in signup direct
    return scheduleEvaluationFromSignupV3({
      isResumeFlow: false,
      address: { state: input.state as StateAbbrev },
      amount: input.amount,
      schedules,
      schedulesTimezone: mapTimezoneToAPIName(input.evaluationTimezone),
      clientFirstName,
      clientLastName,
      contactFirstName: input.firstName,
      contactLastName: input.lastName,
      contactTimeZone: mapTimezoneToAPIName(input.evaluationTimezone),
      dob: extractDate(input.miniScreenerInfo.dob),
      email: input.email,
      isClientMinor: input.target === 'child',
      phone: { mobile: removeWhitespaces(input.phoneNumber) },
      relationshipToClient: input.miniScreenerInfo.relationshipToClient ?? ContactRelationship.Client,
      skipStripePayment: skipPayment,
      leadID: input.leadID,
      ...(skipPayment
        ? {
            entity: input.insuranceForm.entity,
            primaryInsurance: {
              insuredFirstName: input.insuranceForm.firstName,
              insuredLastName: input.insuranceForm.lastName,
              insuredDob: input.insuranceForm.dob as Date,
              insuredRelationshipToClient: MEDICAID_RELATIONSHIP_TO_INSURED_NUMBER,
              insurancePayer: input.insuranceForm.payerId,
              insurancePayerName: input.insuranceForm.payerName,
              insuredPayerType: PayerTypes.MEDICAID,
              insuredMemberID: input.insuranceForm.memberId,
              insuredGroupID: input.insuranceForm.groupId,
            },
          }
        : {
            stripeAccountID: input.stripeAccountId,
            stripePaymentMethodID: input.stripePaymentMethodId,
          }),
    });
  },
);

/** In ms  */
const UPDATE_LEAD_DEBOUNCE_TIMER = 2000;

const getDesiredPaymentMethod = (useInsurance?: boolean): SignupLeadV4['desiredPaymentMethod'] | undefined => {
  if (isUndefined(useInsurance)) return undefined;

  return useInsurance ? 'Insurance' : 'self-pay';
};

export const updateLead = debounce(
  (context: IncompleteSignupV4Data) => {
    const { miniScreenerInfo, insuranceInfo } = context;
    const skipPayment =
      insuranceInfo?.useInsurance === true && insuranceInfo.insuranceEligibility === EligibilityCheckResult.IN_NETWORK;
    const insuranceType = getInsuranceType(
      insuranceInfo?.insuranceType as InsuranceType,
      insuranceInfo?.useInsurance === true,
    );

    return queryClient.executeMutation({
      mutationFn: () =>
        updateSalesforceLead({
          id: context.leadID as string,
          schedulingId: context.schedulingID as string,
          address: context.state
            ? {
                state: context.state as string,
              }
            : undefined,
          firstName: context.target === 'child' ? miniScreenerInfo?.clientFirstName : context.firstName,
          lastName: context.target === 'child' ? miniScreenerInfo?.clientLastName : context.lastName,
          email: context.email,
          dob: context.miniScreenerInfo?.dob,

          phone: context.phoneNumber,
          serviceFor: context.target,

          communicationConcerns: miniScreenerInfo?.concerns
            ? mergeCommunicationConcerns(miniScreenerInfo.concerns)
            : undefined,
          detectedAreas: miniScreenerInfo?.detectedAreas,

          desiredPaymentMethod: getDesiredPaymentMethod(context.insuranceInfo?.useInsurance),
          primaryLanguage: context.miniScreenerInfo?.primaryLanguage,
          relationshipToClient: context.miniScreenerInfo?.relationshipToClient,
          receivedSpeechTherapyBefore: context.miniScreenerInfo?.previousEvaluation,
          primaryPhysician: context.insuranceInfo?.primaryPhysician,
          leadSource: 'Self-Referral - Web Inquiry',
          evaluationDate: context.scheduleInfo?.evaluationDate,
          evalAuthRequired: skipPayment ? 'Yes' : 'No',
          visitAuthRequired: skipPayment ? 'Yes' : 'No',
          physicianOrderRequired: skipPayment ? 'Yes' : 'No',
          memberID: context.insuranceInfo?.insuranceForm?.memberId,
          insuranceType,
          subscriber: context.insuranceInfo?.insuranceForm
            ? {
                firstName: context.insuranceInfo.insuranceForm.firstName,
                lastName: context.insuranceInfo.insuranceForm.lastName,
                dateOfBirth: context.insuranceInfo.insuranceForm.dob as Date,
                relationshipToClient: SUBSCRIBER_RELATIONSHIP_CODE_MAP[InsuranceSubscriberRelationship.Self],
              }
            : undefined,
          selfSignUp: false,
        }),
      mutationKey: 'post-signup-lead',
    });
  },
  UPDATE_LEAD_DEBOUNCE_TIMER,
  {
    maxWait: 3000,
    leading: true,
  },
);

export const checkEligibilityLogic = fromPromise<EligibilityCheckResultWithPCP, EnterInsuranceFormV4>(({ input }) => {
  return checkEligibility({
    checkParams: mapSignupV4ToMedicaidInsuranceForm(input),
    insuranceAllowSelfSignUp: input.allowSelfSignUp,
    insuranceInNetwork: input.networkStatus as TypeInsuranceFields['networkStatus'],
  });
});
